/**
 * Wave O1 (decision 0060): Minga's workflow text as Agent Skills, one source of truth per skill. The cloud's external
 * MCP server serves the same bytes four ways: the official skills extension (`io.modelcontextprotocol/skills`:
 * `skills/list`, `skills/get`, `resources/read skill://<name>/SKILL.md`), plain `resources/list` + `resources/read`, the
 * read-only `guide {name}` tool, and the `onboard` / `hand_off` prompts (wave spec §2.3).
 *
 * Rendering is pure and deterministic: the only placeholder is `{{origin}}` (install and pairing URLs differ between
 * production and local e2e), so the bytes and their digest are stable per deployment. Dates, project ids and user data
 * never go into a skill file; `guide` returns them next to the text. Digests (`sha256:<hex>` of the UTF-8 bytes) are
 * computed by the cloud with core's `sha256Hex`.
 */
import { Schema } from "effect"
import { capture } from "./capture"
import { handOff } from "./hand-off"
import { importDocs } from "./import-docs"
import { onboardProject } from "./onboard-project"
import { recallProbe } from "./recall-probe"
import { runnerSetup } from "./runner-setup"
import { status } from "./status"
import { workAnItem } from "./work-an-item"

/** One skill's source. `body` is markdown with `{{origin}}` as the only placeholder; no frontmatter. */
export interface SkillSource {
  /** Lowercase letters, digits and hyphens (≤ 64); equals the directory segment of its URI. */
  readonly name: string
  /** For `resources/list` (`title`); not part of the frontmatter. */
  readonly title: string
  /** What the skill does and when to use it (≤ 1,024 characters). */
  readonly description: string
  readonly body: string
}

/** The extension's identifier, declared under `capabilities.extensions` (with `{}`: no directory reads). */
export const SKILLS_EXTENSION = "io.modelcontextprotocol/skills"
/** `mimeType` of every skill file ("SHOULD be text/markdown"). */
export const SKILL_MIME_TYPE = "text/markdown"
/** The origin `importPrompt` and tests assume when none is given. */
export const DEFAULT_ORIGIN = "https://app.minga.run"

export const SKILL_NAMES = [
  "onboard-project",
  "runner-setup",
  "capture",
  "status",
  "work-an-item",
  "hand-off",
  // wave DOC (decision 0068)
  "import-docs",
  // decision 0139 (memory phase 1): recall checks as ordinary recurring work with a KPI
  "recall-probe",
] as const
export const SkillName = Schema.Literals(SKILL_NAMES)
export type SkillName = typeof SkillName.Type

export const SKILLS: Readonly<Record<SkillName, SkillSource>> = {
  "onboard-project": onboardProject,
  "runner-setup": runnerSetup,
  capture,
  status,
  "work-an-item": workAnItem,
  "hand-off": handOff,
  "import-docs": importDocs,
  "recall-probe": recallProbe,
}

export const isSkillName = (s: string): s is SkillName => (SKILL_NAMES as ReadonlyArray<string>).includes(s)

/** `skill://<name>/SKILL.md`: the resource URI of a skill's only file. */
export const skillUri = (name: SkillName): string => `skill://${name}/SKILL.md`

/** The skill a `SKILL.md` URI names, or null (unknown skill, another file, another scheme: the cloud answers -32602). */
export const skillNameOfUri = (uri: string): SkillName | null => {
  const m = /^skill:\/\/([a-z0-9-]+)\/SKILL\.md$/.exec(uri)
  const name = m?.[1]
  return name !== undefined && isSkillName(name) ? name : null
}

/** An origin without a trailing slash ("https://app.minga.run", "http://localhost:8799"). */
const cleanOrigin = (origin: string): string => origin.replace(/\/+$/, "")

/** The skill's body with `{{origin}}` filled in (no frontmatter): what the prompts wrap. */
export const skillBody = (name: SkillName, origin: string): string =>
  SKILLS[name].body.replaceAll("{{origin}}", cleanOrigin(origin))

/** The frontmatter fields, exactly `name` and `description` (the extension's `frontmatter`, unchanged). */
export const skillFrontmatter = (
  name: SkillName,
): { readonly name: string; readonly description: string } => ({
  name: SKILLS[name].name,
  description: SKILLS[name].description,
})

/**
 * The full `SKILL.md`: YAML frontmatter (`name` plain, `description` as a double-quoted scalar, which is the JSON string
 * form and always valid YAML), a blank line, the body. Ends with one newline.
 */
export const renderSkill = (name: SkillName, origin: string): string => {
  const fm = skillFrontmatter(name)
  const body = skillBody(name, origin).replace(/\s+$/, "")
  return `---\nname: ${fm.name}\ndescription: ${JSON.stringify(fm.description)}\n---\n\n${body}\n`
}

/**
 * The frontmatter of a rendered `SKILL.md` as key → string: the subset of YAML `renderSkill` writes (one `key: value`
 * per line, plain or double-quoted). Null when the text has no frontmatter block or a line is outside that subset.
 * The cloud and the tests use it to check that `frontmatter` equals the parsed file.
 */
export const parseSkillFrontmatter = (text: string): Record<string, string> | null => {
  const m = /^---\n([\s\S]*?)\n---\n/.exec(text)
  if (!m) return null
  const out: Record<string, string> = {}
  for (const line of (m[1] ?? "").split("\n")) {
    const kv = /^([A-Za-z][A-Za-z0-9_-]*):[ ]+(.*)$/.exec(line)
    if (!kv) return null
    const [, key = "", raw = ""] = kv
    if (raw.startsWith('"')) {
      try {
        const v: unknown = JSON.parse(raw)
        if (typeof v !== "string") return null
        out[key] = v
      } catch {
        return null
      }
    } else out[key] = raw
  }
  return out
}

// #region wire shapes (the extension as published, read 2026-10-11; wave spec §2.1)

/** `sha256:` and 64 lowercase hex characters over the file's raw bytes. */
export const SkillDigest = Schema.String.check(Schema.isPattern(/^sha256:[0-9a-f]{64}$/))

/** One file of a skill's manifest. */
export const SkillFile = Schema.Struct({
  uri: Schema.String,
  digest: SkillDigest,
  /** Byte size of the raw file. */
  size: Schema.Int.check(Schema.isGreaterThanOrEqualTo(0)),
})
export type SkillFile = typeof SkillFile.Type

/** An entry of `skills/list` and `result.skill` of `skills/get`. */
export const SkillEntry = Schema.Struct({
  /** The resource URI of the skill's `SKILL.md`. */
  uri: Schema.String,
  /** All YAML frontmatter fields, unchanged (Minga: exactly `name` and `description`). */
  frontmatter: Schema.Record(Schema.String, Schema.Unknown),
  /** The complete file manifest (Minga never serves `"dynamic"` skills). */
  resources: Schema.Union([Schema.Array(SkillFile), Schema.Literal("dynamic")]),
})
export type SkillEntry = typeof SkillEntry.Type

/** "List and get results MUST include resultType: complete, ttlMs, and cacheScope." */
const CacheFields = {
  resultType: Schema.Literal("complete"),
  ttlMs: Schema.Int.check(Schema.isGreaterThanOrEqualTo(0)),
  cacheScope: Schema.Literals(["public", "private"]),
}

export const SkillsListResult = Schema.Struct({
  ...CacheFields,
  skills: Schema.Array(SkillEntry),
  nextCursor: Schema.optionalKey(Schema.String),
})
export type SkillsListResult = typeof SkillsListResult.Type

export const SkillsGetResult = Schema.Struct({ ...CacheFields, skill: SkillEntry })
export type SkillsGetResult = typeof SkillsGetResult.Type

/** `skills/list` params: may be absent; `cursor` and `_meta` are the only keys the spec names (others are ignored). */
export const SkillsListParams = Schema.Struct({
  cursor: Schema.optionalKey(Schema.String),
  _meta: Schema.optionalKey(Schema.Record(Schema.String, Schema.Unknown)),
})
export type SkillsListParams = typeof SkillsListParams.Type

/** `skills/get` params: the `SKILL.md` URI. */
export const SkillsGetParams = Schema.Struct({
  uri: Schema.String,
  _meta: Schema.optionalKey(Schema.Record(Schema.String, Schema.Unknown)),
})
export type SkillsGetParams = typeof SkillsGetParams.Type

/** The cache hints Minga sends on every skill result and cacheable list (the same for every person). */
export const SKILL_CACHE = { ttlMs: 300_000, cacheScope: "public" } as const

// #endregion wire shapes
